import React, { useEffect, useMemo, useState } from 'react';
import Input from '../../Shared/Input';
import ReactMarkdown from 'react-markdown';
import submitMarketingForm from '../../../services/submitMarketingForm';
import ModalFormStyled from './ModalForm.styled';
import Close from '../../Shared/Icons/close';
import Button from '../../Shared/Button';

const orderFormFields = fields => {
  const order = {
    Email: 2,
    'First Name': 0,
    'Last Name': 1,
    'ZIP Code': 3,
    'Phone Number': 4,
  };

  const sortedOutput = new Array(fields.length);
  Object.keys(order).forEach(orderKey => {
    const orderIndex = order[orderKey];
    if (orderIndex !== undefined && fields.some(field => field === orderKey)) {
      sortedOutput[orderIndex] = orderKey;
    }
  });

  return sortedOutput;
};

const configureFormFields = fields => {
  const sortedFields = [...orderFormFields(fields)];

  const onBeforePhoneChange = value => {
    const newValue = value.replace(/[^0-9\-]/gim, '');

    const numbersOnly = newValue.replace(/\-/gim, '');
    const split = numbersOnly.split('');
    if (numbersOnly.length >= 3 && newValue.length > 3) split.splice(3, 0, '-');
    if (numbersOnly.length >= 6 && newValue.length > 7) split.splice(7, 0, '-');

    if (numbersOnly.length < 11) return split.join('');
  };

  const configuration = {
    Email: {
      name: 'Email',
      parameterName: 'emailAddress',
      required: true,
      format: /^\S{1,}@\S{1,}\.\S{1,}$/gim,
      type: 'text',
      placeholder: 'Email',
      className: 'modal-header-form-input modal-header-form-input__email',
      min: 5,
      max: 100,
    },
    'First Name': {
      name: 'First Name',
      parameterName: 'firstName',
      required: true,
      format: null,
      type: 'text',
      placeholder: 'First Name',
      className: 'modal-header-form-input modal-header-form-input__first-name',
      min: 1,
      max: 100,
    },
    'Last Name': {
      name: 'Last Name',
      parameterName: 'lastName',
      required: true,
      format: null,
      type: 'text',
      placeholder: 'Last Name',
      className: 'modal-header-form-input modal-header-form-input__last-name',
      min: 1,
      max: 100,
    },
    'ZIP Code': {
      name: 'ZIP Code',
      parameterName: 'zipCode',
      required: true,
      format: /[a-z0-9\-]/gim,
      type: 'text',
      placeholder: 'Zip Code',
      className: 'modal-header-form-input modal-header-form-input__zip-code',
      min: 1,
      max: 20,
    },
    'Phone Number': {
      name: 'Phone Number',
      parameterName: 'phoneNumber',
      required: true,
      format: /[0-9\-]/,
      type: 'text',
      placeholder: 'Phone Number',
      className: 'modal-header-form-input modal-header-form-input__email',
      min: 1,
      max: 12,
      onBeforeChange: onBeforePhoneChange,
    },
  };

  return sortedFields
    .map(field => configuration[field] || null)
    .filter(config => config);
};

interface WebForm {
  buttonAndTextColors?: string;
  firstFormButtonText?: string;
  formContactFields?: string[];
  formHeader?: string;
  formSubHeader?: string;
  formIntro?: string;
  firstFormButtonURL?: string;
  secondFormButtonText?: string;
  secondFormButtonURL?: string;
  secondButtonColor?: string;
  disclaimer?: string;
  originWebApp?: string;
  originWebPageType?: string;
  originWebFormType?: string;
  originCampaign?: string;
  originWebFormCategory?: string;
  dataExtension?: string;
}

interface InputState {
  [key: string]: {
    value: string;
    touched: boolean;
    error: string;
    configuration: {
      parameterName: string;
      name: string;
      required?: boolean;
      format?: RegExp;
      type: string;
      placeholder: string;
      className: string;
      min?: number;
      max?: number;
      onBeforeChange?: (value: string) => string;
    };
  };
}

interface ModalFormProps {
  webForm?: WebForm;
  imageSrc?: string;
  imageAlt?: string;
  className?: string;
  listItems?: Array<string>;
  webModalOpen?: boolean;
  isSubmittingForm?: boolean;
  onSubmittingFormChange?: (state: boolean) => void;
  setWebModalOpen?: (state: boolean) => void;
  setFormSubmitted?: (state: boolean) => void;
  triggerGTMEvent?: (eventData: {
    name: string;
    location: string;
    category: string;
    action: string;
    label: string;
  }) => void;
}

const ModalForm: React.FC<ModalFormProps> = ({
  webForm = {} as WebForm,
  imageSrc = '',
  className = 'modal-web-form',
  setWebModalOpen,
  setFormSubmitted,
  listItems = [],
}) => {
  const [inputs, updateInputs] = useState<InputState>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [submittingForm, setSubmittingForm] = useState<boolean>(false);

  const inputConfigurations = useMemo(
    () => configureFormFields(webForm?.formContactFields),
    [webForm?.formContactFields]
  );

  useEffect(() => {
    if (
      Object.keys(inputConfigurations).length > 0 &&
      Object.keys(inputs).length === 0
    ) {
      const defaultInputValues = Object.values(inputConfigurations).reduce(
        (defaultValue, configuration) => {
          if (!defaultValue[configuration.name]) {
            defaultValue[configuration.name] = {
              value: '',
              touched: false,
              error: '',
              configuration,
            };
          }
          return defaultValue;
        },
        {} as InputState
      );

      updateInputs(defaultInputValues);
    }
  }, [inputConfigurations]);

  const handleInputChange = (input: InputState[keyof InputState]) => {
    updateInputs(previousState => ({
      ...previousState,
      [input.configuration.name]: {
        ...previousState[input.configuration.name],
        value: input.value,
        touched: true,
        error: '',
      },
    }));

    checkInputValidity();
  };

  const validateInputs = (): boolean => {
    let hasError = false;
    const newInputs = { ...inputs };

    const addError = (key: string, errorMsg: string) => {
      newInputs[key].error = errorMsg;
      newInputs[key].touched = true;
      hasError = true;
    };

    Object.keys(inputs).forEach(key => {
      const input = inputs[key];
      const { value, configuration: config } = input;

      if (config.required && !value.trim()) {
        addError(key, `${config.name} is required.`);
      }
      if (typeof config.max === 'number' && value.length > config.max) {
        addError(key, `${config.name} is too long.`);
      }
      if (typeof config.min === 'number' && value.length < config.min) {
        addError(key, `${config.name} is too short.`);
      }
      if (config.format && !value.match(config.format)) {
        addError(key, `Invalid ${config.name} format.`);
      }
    });

    updateInputs(newInputs);

    return !hasError;
  };

  const submitForm = () => {
    if (!validateInputs()) {
      return;
    }

    setSubmittingForm(true);
    setErrorMessage('');

    // Create values from form inputs
    const formValues = Object.values(inputs).reduce(
      (acc, input) => ({
        ...acc,
        [input.configuration.parameterName]: input.value,
      }),
      {}
    );

    // Add metadata from webForm
    const values = {
      ...formValues,
      originWebFormType: webForm.originWebFormType,
      originWebPageType: webForm.originWebPageType,
      originWebApp: webForm.originWebApp,
      dataExtension: webForm.dataExtension,
      originWebUrl: window.location.href,
      originCampaign: webForm.originCampaign,
    };

    submitMarketingForm(values)
      .then(response => {
        setSubmittingForm(false);

        if (response?.status === 200) {
          setFormSubmitted(true);
          closeModal();
        } else {
          throw new Error('Form submission failed.');
        }
      })
      .catch(err => {
        setSubmittingForm(false);
        setErrorMessage('An error occurred while submitting the form.');
        console.error(err);
      });
  };

  const closeModal = () => {
    setWebModalOpen(false);
  };

  const checkInputValidity = () => {
    const allValid = Object.values(inputs).every(input => {
      if (input.configuration.required) {
        return input.value.trim() !== '';
      }
      return true;
    });
    setIsButtonDisabled(!allValid);
  };

  return (
    <ModalFormStyled
      imageSrc={imageSrc}
      className={className}
      submittingForm={submittingForm}
      buttonAndTextColors={webForm?.buttonAndTextColors}
      id='modal'
    >
      <div className='modal-header-form'>
        <div className='modal-header-form__heading'>
          {webForm?.formHeader && (
            <h4 className='modal-header-form__header'>{webForm?.formHeader}</h4>
          )}
          <div className='close-button' onClick={closeModal}>
            <Close />
          </div>
        </div>
        <div className='modal-header-form__body'>
          {errorMessage && (
            <p className='modal-header-form__error type-h4'>{errorMessage}</p>
          )}
          {webForm?.formSubHeader && (
            <h6 className='modal-header-form__sub-header'>
              {webForm.formSubHeader}
            </h6>
          )}
          {listItems && (
            <div className='modal-header-form__list'>
              <ul>
                {listItems.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          <form
            onSubmit={e => {
              e.preventDefault();
              submitForm();
            }}
            className='modal-header-form__form-body'
          >
            {inputConfigurations.map(({ name }, index) => {
              const input = inputs[name];

              if (input) {
                const onChange = e => {
                  const beforeChange = input.configuration.onBeforeChange
                    ? input.configuration.onBeforeChange(e.target.value)
                    : e.target.value;

                  const newValue = {
                    ...input,
                    value: beforeChange,
                  };

                  handleInputChange(newValue);
                };

                return (
                  <Input
                    className={input.configuration.className}
                    key={`modal-form-input-${index}`}
                    type={input.configuration.type}
                    placeholder={input.configuration.placeholder}
                    touched={input.touched}
                    error={input.error}
                    value={input.value}
                    onChange={onChange}
                    maxLength={input.configuration.max}
                    minLength={input.configuration.min}
                    disabled={submittingForm}
                  />
                );
              }
            })}
            {webForm?.disclaimer && (
              <div className='modal-header-form__disclaimer'>
                <ReactMarkdown source={webForm.disclaimer} />
              </div>
            )}
            <div className='modal-header-form__submit-button'>
              <Button
                onClick={submitForm}
                disabled={isButtonDisabled || submittingForm}
              >
                {webForm?.firstFormButtonText}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </ModalFormStyled>
  );
};

export default ModalForm;
